import { MarketTextareaCustomEvent } from '@market/web-components';
import { MarketTextarea } from '@squareup/dex-ui-shared-market';
import { toIgnitionDataType } from '@squareup/dex-utils-oas';
import { AnyFieldOptions } from '@squareup/dex-utils-shared-api-explorer';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { usePrimitiveProps } from '../hooks/nullable-hooks';
import { ApiExplorerPrimitiveFieldProps } from '../props';

const objectPlaceholderSchema = {
  key_1: 'value_1',
  key_2: 'value_2',
};
const arrayPlaceholderSchema = ['value_1', 'value_2'];
const jsonFieldPlaceholderMap = {
  [toIgnitionDataType(AnyFieldOptions.OBJ.toLowerCase() as 'object')]:
    JSON.stringify(objectPlaceholderSchema, undefined, 4),
  [toIgnitionDataType(AnyFieldOptions.ARR.toLowerCase() as 'array')]:
    JSON.stringify(arrayPlaceholderSchema, undefined, 4),
};

interface Props extends ApiExplorerPrimitiveFieldProps {
  placeholder?: string;
}

const ApiExplorerJsonField: FC<Props> = ({
  currentValue,
  onChange,
  path,
  placeholder,
  name,
  type,
}) => {
  const [userInput, setUserInput] = useState<string | null>(
    // If the current value is undefined, set the user input to null
    // When set to null, the text area will display the placeholder
    currentValue === undefined ? null : (currentValue as string)
  );
  const ref = useRef(type);

  const onTextAreaChangeCallback = useCallback(
    ({
      detail: { value },
    }: MarketTextareaCustomEvent<{
      value: string;
      originalEvent: KeyboardEvent;
    }>): void => {
      if (value === '') {
        setUserInput(null);
        onChange(undefined, path);
        return;
      }

      // Record user input and use as the text area value
      setUserInput(value);
      try {
        const newValue = JSON.parse(value as string);
        onChange(newValue, path);
      } catch {
        onChange(value, path);
      }
    },
    [onChange, path]
  );

  useEffect(() => {
    // Reset the user input when the type changes
    // Compare the current type with the previous type to avoid reset on initial render
    if (type !== ref.current) {
      ref.current = type;
      setUserInput(null);
    }
  }, [type]);

  const textPlaceholder = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }
    if (type && type in jsonFieldPlaceholderMap) {
      return jsonFieldPlaceholderMap[type] as string;
    }
    return '';
  }, [placeholder, type]);

  const { placeholder: updatedPlaceholder } = usePrimitiveProps(
    textPlaceholder,
    currentValue
  );

  return (
    <MarketTextarea
      data-testid="api-explorer-json-field"
      onMarketTextareaValueChange={onTextAreaChangeCallback}
      placeholder={updatedPlaceholder}
      name={name}
      value={userInput as string}
    />
  );
};

export { ApiExplorerJsonField, jsonFieldPlaceholderMap };
